import bind from 'bind-decorator';
import { observable } from 'mobx';
import { createAccount, changeAccount } from 'services/Accounts';
import { getClientId, express } from 'services/Onboarding';
import { AccountsStore } from 'stores/Accounts';
import { goToConnectPage } from 'services/Stripe';
import { Account } from 'models/Account';
import { AuthStore } from 'stores/Auth';
import { GlobalStore } from 'stores/Global';
import { AccountUserRoles } from 'models/AccountUser';
import { DEFAULT_LOGO } from 'utils/Logo';

export class AccountCreateModel {
  @observable step: number = 0;
  @observable loading: boolean = false;
  @observable connectLoading: boolean = false;
  @observable connectSuccess: boolean = false;
  @observable connectError: string = '';
  @observable bankConnected: boolean = false;

  name: string = '';
  slug: string = '';
  logo: string = '';
  address: string = '';
  accentColor: string = '';

  accountId: string = '';
  continuation: boolean = false;
  finance: boolean = false;
  // Stripe ledger for the new account. Admin-only and create-only; the API rejects any attempt
  // to change it afterwards, since a test-mode acct_* is not valid against a live key.
  // Deliberately unset - whether a merchant takes real money is an explicit choice, not a default.
  mode: string = '';

  formControl: any = null;

  constructor(options?: { step: number, account: Account, code?: string, state?: string }) {
    if (options) {
      this.name = options.account.name;
      this.slug = options.account.slug || '';
      this.logo = options.account.logo;
      this.address = options.account.address;
      this.accentColor = options.account.accentColor;
      this.accountId = options.account.accountId;
      // Without this, isDemo is false whenever the model is built from a stored account - which
      // is how the /banking and account routes construct it.
      this.mode = options.account.mode || '';
      this.finance = options.account.role === AccountUserRoles.FINANCE;

      this.step = options.step;
      this.continuation = true;

      if (options.code && options.state === options.account.accountId) {
        this.connectStripeByCode(options.code);
      }
    }
  }

  async connectStripeByCode(stripeAuthCode: string) {
    this.connectLoading = true;

    try {
      await express({
        accountId: this.accountId,
        stripeAuthCode
      });
      await AccountsStore.fetchAccounts();

      this.bankConnected = true;
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error.message) {
        this.connectError = e.response.data.error.message;
      } else {
        this.connectError = 'Unknown error, please try again later.'
      }
    }

    this.connectLoading = false;
  }

  /** Demo accounts have no Stripe presence, so the Bank Account step does not apply to them. */
  get isDemo(): boolean {
    return this.mode === 'Demo';
  }

  @bind
  next() {
    if (this.step === 0 && this.formControl) {
      return this.formControl.submit();
    }

    // Step 1 is Bank Account - skip it for demo accounts, whose Next button would otherwise never
    // enable because bankConnected can never become true.
    this.step = this.step + (this.isDemo && this.step === 0 ? 2 : 1);
  }

  @bind
  prev() {
    this.step = this.step - 1;
  }

  @bind
  async handleConnect() {
    this.connectLoading = true;

    try {
      const response = await getClientId(this.accountId);
      const clientId = response.data.data;
      const currentUser = AuthStore.currentUser;

      goToConnectPage(clientId, this.accountId, {
        email: currentUser.email,
        name: currentUser.name
      });
    } catch (e) {
    }

    this.connectLoading = false;
  }

  @bind
  async finishCreate(values: any) {
    this.loading = true;

    this.name = values.name;
    this.slug = values.slug;
    // The API requires a logo, so fall back to the bundled placeholder rather than forcing an
    // upload just to get an account created.
    this.logo = values.logo || DEFAULT_LOGO;
    this.address = values.address;
    this.accentColor = values.accentColor;
    this.mode = values.mode || this.mode;

    let error = null;

    try {
      if (this.accountId) {
        const response = await changeAccount(this.accountId, {
          name: this.name,
          logo: this.logo,
          slug: this.slug,
          address: this.address,
          accentColor: this.accentColor
        });
        const account = AccountsStore.getAccount(this.accountId);

        if (account) {
          account.name = response.data.data.name;
          account.slug = response.data.data.slug;
          account.logo = response.data.data.logo;
          account.address = response.data.data.address;
          account.accentColor = response.data.data.accentColor;
        }
      } else {
        const response: any = await createAccount({
          name: this.name,
          logo: this.logo,
          slug: this.slug,
          address: this.address,
          accentColor: this.accentColor,
          // Create-only: omitted from the update call above, which would be rejected.
          mode: this.mode
        });

        this.accountId = response.data.data.accountId;

        AccountsStore.accounts.push(new Account({ ...response.data.data, role: AccountUserRoles.OWNER }));

        GlobalStore.setCurrentAccountId(this.accountId);
      }
    } catch (e) {
      error = e;
    }

    this.loading = false;

    if (!error) {
      // Skip the Bank Account step for demo accounts - see next().
      this.step = this.step + (this.isDemo && this.step === 0 ? 2 : 1);
    }
  }
}